import { useId, useState } from 'react';
import type { ChangeEvent, FocusEvent, InputHTMLAttributes, JSX, KeyboardEvent, MouseEvent } from 'react';
import { useFieldAria } from './Field';
import { CONTROL_HEIGHT, FOCUS_RING, INPUT_BASE, cx } from './styles';

/*
 * ============================================================================
 * 数值输入：这里的取舍都是被"地面站"这个场景逼出来的，逐条写清楚
 * ============================================================================
 *
 * 值模型：`value: number | null`
 *   `null` = "没有值 / 还没读到"，`0` = "值就是零"。这两件事必须分开 ——
 *   高度 0 米和"高度还没读上来"在下发参数时是完全不同的结论，把读不到当 0
 *   写进去，是这类软件里最危险的一类 bug。清空输入框因此提交 `null`，不是 0。
 *
 * 文本模型：解析结果是三态，不是 `number | null`
 *   empty（空）/ number / invalid（非法）。把"空"和"非法"混成一个 null，
 *   就没法区分"用户清空了"和"用户粘进来一坨非数字"，前者要提交、后者不能提交。
 *
 * 为什么用白名单正则，而不是直接 `Number(text)`：
 *   `Number('')` 是 0、`Number('   ')` 是 0  → 清空输入框会被静默当成"设为 0"；
 *   `Number('0x10')` 是 16                  → 十六进制被当成十进制接受；
 *   `Number('Infinity')` 是 Infinity        → 无穷大能进到参数里；
 *   `Number('1e5')` 是 100000               → 这个我们要**接受**（见下）。
 * 所以先判形状，再转数值。
 *
 * 科学计数法：接受
 *   参数表里的默认值、日志里复制出来的值都可能是 `1.5e-3`，键盘输入 "1e3" 也是
 *   很自然的表达。形状不合法才拒绝，不因为"看起来不像常规小数"就拒绝。
 *
 * 全角字符：归一化成半角
 *   中文输入法下极易打出全角数字 `１２` 和全角小数点 `．`；`U+2212`（排版减号）
 *   常见于从文档/PDF 粘贴。它们**看起来就是数字**，所以直接按数字处理 ——
 *   报"请输入数字"让用户自己去找哪个字符不对，是纯粹的折磨。
 *
 * 千分位逗号：不去掉
 *   `1,5` 在中文习惯里是 1500，在欧洲习惯里是 1.5，猜错的代价是 1000 倍。
 *   宁可判为非法，让用户明确写 `1500`。数值输入里没有"聪明的猜"这个选项。
 *
 * 超出 min/max：不自动截断，只标记非法
 *   输入时截断会让用户根本没法输入 100 中的 "1"（一敲就被夹到下限）。
 *   提交时也不夹取 —— 静默改值意味着"我写进去的"和"实际生效的"不一致，
 *   在地面站里不可接受。越界就显式报出来，由调用方决定是拒绝还是改写。
 *
 * 精度与步长：`decimals` 只约束**显示**和**步进舍入**，不改写用户输入
 *   步进（方向键/上下按钮）会产生浮点噪声：0.1 步长从 1.2 加一次会得到
 *   1.2000000000000002。所以步进结果要收尾。但用户手输的 1.23456 原样提交 ——
 *   静默四舍五入等于篡改用户的输入值。
 *
 * 提交时机：`commitOn`
 *   `change`（默认）：每一次可解析的输入都通知调用方，适合"改了立刻生效"的本地读数；
 *   `blur`：只在回车/失焦时通知，适合"写进机体参数"这类有副作用的动作 ——
 *           参数表里每敲一个字符就下发一次，是要出事的。
 */

export type NumericParse =
  | { kind: 'empty' }
  | { kind: 'number'; value: number }
  | { kind: 'invalid' };

const NUMERIC_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** 全角 → 半角（只做字符映射，不做数值猜测），并去掉首尾空白 */
export function normalizeNumericText(text: string): string {
  let out = '';
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= 0xff10 && code <= 0xff19) out += String.fromCharCode(code - 0xff10 + 0x30);
    else if (char === '．') out += '.';
    else if (char === '－' || char === '−') out += '-';
    else if (char === '＋') out += '+';
    else out += char;
  }
  return out.trim();
}

export function parseNumericText(text: string): NumericParse {
  const normalized = normalizeNumericText(text);
  if (normalized === '') return { kind: 'empty' };
  if (!NUMERIC_TEXT.test(normalized)) return { kind: 'invalid' };
  const value = Number(normalized);
  // `1e999` 形状合法但溢出成 Infinity：数值上没有意义，按非法处理
  if (!Number.isFinite(value)) return { kind: 'invalid' };
  return { kind: 'number', value };
}

/** 步进后的收尾：去掉 15 位有效数字之外的浮点噪声，必要时再按 `decimals` 收 */
function tidyStep(value: number, decimals: number | undefined): number {
  const cleaned = Number.parseFloat(value.toPrecision(15));
  if (decimals === undefined) return cleaned;
  return Number(cleaned.toFixed(Math.max(0, Math.min(12, decimals))));
}

/** 显示用的格式化。`decimals` 给了就按固定小数位（"1.20" 而不是 "1.2"），列对得齐 */
export function formatNumericValue(value: number | null, decimals?: number): string {
  if (value === null || !Number.isFinite(value)) return '';
  if (decimals === undefined) return String(value);
  return value.toFixed(Math.max(0, Math.min(12, decimals)));
}

function rangeTextOf(min: number | undefined, max: number | undefined, unit: string | undefined): string {
  const suffix = unit === undefined ? '' : ` ${unit}`;
  if (min !== undefined && max !== undefined) return `允许范围 ${min} ~ ${max}${suffix}`;
  if (min !== undefined) return `不小于 ${min}${suffix}`;
  if (max !== undefined) return `不大于 ${max}${suffix}`;
  return '';
}

export interface NumberFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    | 'aria-describedby'
    | 'children'
    | 'className'
    | 'disabled'
    | 'id'
    | 'inputMode'
    | 'max'
    | 'min'
    | 'onChange'
    | 'required'
    | 'size'
    | 'step'
    | 'type'
    | 'value'
  > {
  /** 已提交的数值。`null` = 没有值（与 `0` 严格区分） */
  value: number | null;
  /** 只在文本可解析时调用；非法文本**不会**触发。步进按钮与方向键会直接调用它 */
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  /** 显示与步进的小数位；不用于改写用户输入 */
  decimals?: number;
  /** 单位后缀（`m` / `m/s`）。只是视觉与说明，不进入 value */
  unit?: string;
  commitOn?: 'change' | 'blur';
  showStepper?: boolean;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  id?: string;
  inputMode?: 'decimal' | 'numeric' | 'text';
  'aria-describedby'?: string;
}

export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  decimals,
  unit,
  commitOn = 'change',
  showStepper = false,
  invalid = false,
  required = false,
  disabled = false,
  size = 'md',
  className,
  id,
  inputMode,
  'aria-describedby': ariaDescribedBy,
  onKeyDown,
  onBlur,
  onFocus,
  ...rest
}: NumberFieldProps): JSX.Element {
  const field = useFieldAria();
  const generatedId = useId().replaceAll(':', '');
  const controlId = field === null ? (id ?? `number-${generatedId}`) : field.controlId;
  const unitId = `${controlId}-unit`;
  const messageId = `${controlId}-message`;

  /*
   * 编辑草稿。
   *
   * 这是本组件唯一的一处内部状态，边界写清楚：它**只**是"用户正在敲的这段文本"，
   * 不持有值的副本。三条规则保证界面不会和 value 长期不一致：
   *   1. 失焦 / 回车提交后草稿清空，显示完全由 value 推导；
   *   2. Escape 放弃本次编辑，显示立刻回到 value；
   *   3. 外部把 value 改成"草稿解析值之外"的另一个数（父组件夹取、取整、或拒绝了
   *      我们的提交）时，草稿立即作废 —— 外部权威优先。
   * 不能无条件丢弃草稿：我们自己 onChange 出去的 1.5 会原样回流到 value，
   * 那样"1." → "1.5" 的逐字输入会在每敲一下时被打断，光标乱跳。
   */
  const [draft, setDraft] = useState<string | null>(null);
  const [seenValue, setSeenValue] = useState(value);

  const parsedDraft = draft === null ? null : parseNumericText(draft);
  const draftValue = parsedDraft !== null && parsedDraft.kind === 'number' ? parsedDraft.value : null;

  if (seenValue !== value) {
    setSeenValue(value);
    if (draft !== null && draftValue !== value) setDraft(null);
  }

  const displayValue = draft ?? formatNumericValue(value, decimals);
  const effectiveValue = draft === null ? value : draftValue;
  const parseFailed = parsedDraft !== null && parsedDraft.kind === 'invalid';
  const rangeFailed =
    !parseFailed &&
    effectiveValue !== null &&
    ((min !== undefined && effectiveValue < min) || (max !== undefined && effectiveValue > max));

  /*
   * 自己报的错误只在调用方没有给 `error` 时显示：调用方的文案是权威，
   * 两条红字互相矛盾比只给一条更糟。
   */
  const ownMessage = field?.invalid === true
    ? undefined
    : parseFailed
      ? '请输入数字（例如 12.5）'
      : rangeFailed
        ? rangeTextOf(min, max, unit)
        : undefined;

  const isRequired = required || field?.required === true;
  const isDisabled = disabled || field?.disabled === true;
  const isInvalid = invalid || field?.invalid === true || parseFailed || rangeFailed;
  const describedBy = cx(
    field?.describedBy,
    unit === undefined ? undefined : unitId,
    ownMessage === undefined ? undefined : messageId,
    ariaDescribedBy,
  );

  /** 非法文本**不提交**：宁可什么都不发生，也不要把 NaN 或猜测值写进参数 */
  function commitText(text: string): void {
    const parsed = parseNumericText(text);
    if (parsed.kind === 'invalid') return;
    onChange(parsed.kind === 'empty' ? null : parsed.value);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const text = normalizeNumericText(event.target.value);
    setDraft(text);
    if (commitOn === 'change') commitText(text);
  }

  function applyStep(direction: 1 | -1): void {
    const base = effectiveValue ?? min ?? 0;
    let next = tidyStep(base + direction * step, decimals);
    /*
     * 步进时的夹取与"用户手输越界"的处理不同：越界值是**我们自己**算出来的，
     * 不能把"按一下上键落到 105"这种由组件制造出来的非法值交出去。
     */
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    setDraft(null);
    onChange(next);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowUp') {
      // 阻止默认行为：否则光标会跳到行首，且页面可能跟着滚
      event.preventDefault();
      applyStep(1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      applyStep(-1);
    } else if (event.key === 'Enter' && commitOn === 'blur') {
      // 回车用来提交本次编辑，并拦下隐式表单提交 —— 改一个参数不该顺手提交整页
      event.preventDefault();
      commitText(draft ?? displayValue);
    } else if (event.key === 'Escape') {
      // 放弃这次编辑。输错一个数字时不必先全选删掉再重打
      setDraft(null);
    }
    onKeyDown?.(event);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>): void {
    if (commitOn === 'blur' && draft !== null) commitText(draft);
    // 一次编辑会话结束：显示回到由 value 推导的值
    setDraft(null);
    onBlur?.(event);
  }

  // 保住输入焦点：用户连点几下上下按钮微调时，不该每次都把焦点抢到按钮上
  function keepFocus(event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
  }

  return (
    <div className={cx('flex items-center gap-1', className)}>
      <div className="relative flex flex-1 items-center">
        <input
          {...rest}
          id={controlId}
          type="text"
          inputMode={inputMode ?? 'decimal'}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={onFocus}
          required={isRequired ? true : undefined}
          disabled={isDisabled}
          aria-invalid={isInvalid ? true : undefined}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          /*
           * 不用 `type="number"`：它的值在浏览器里必须是一个合法数字，于是
           * "1."（正在输小数）、"-"（正在输负数）会被浏览器直接吞掉、光标乱跳；
           * 小数点还跟系统 locale 走（逗号地区会拒绝 "."）；
           * 而 `1e3` 这类粘贴行为各家实现都不一样。地面站要的是"我看到的字符就是
           * 我打的字符"，所以走文本 + 自己解析。
           */
          spellCheck={false}
          autoComplete={rest.autoComplete ?? 'off'}
          className={cx(
            INPUT_BASE,
            CONTROL_HEIGHT[size],
            'px-2 py-1 tabular-nums',
            unit === undefined ? undefined : 'pr-8',
            FOCUS_RING,
          )}
        />
        {unit === undefined ? null : (
          // 单位是**真内容**（会被 aria-describedby 读出来），不是装饰
          <span id={unitId} className="pointer-events-none absolute right-2 text-xs text-fg-subtle">
            {unit}
          </span>
        )}
      </div>

      {showStepper ? (
        <span className="inline-flex shrink-0 flex-col gap-0.5">
          {/*
           * 上下按钮是**冗余入口**：方向键已经提供了同样的能力，所以它们
           * `tabIndex={-1}` —— 留在 Tab 序列里只会让"跳过这个字段"多按两次。
           */}
          <button
            type="button"
            tabIndex={-1}
            aria-label="增加"
            onMouseDown={keepFocus}
            onClick={() => applyStep(1)}
            disabled={isDisabled}
            className={cx(
              'flex h-4 w-6 items-center justify-center rounded-[2px] border border-line text-[9px] leading-none text-fg-muted hover:bg-surface-3 disabled:opacity-50',
              FOCUS_RING,
            )}
          >
            <span aria-hidden="true">▲</span>
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="减少"
            onMouseDown={keepFocus}
            onClick={() => applyStep(-1)}
            disabled={isDisabled}
            className={cx(
              'flex h-4 w-6 items-center justify-center rounded-[2px] border border-line text-[9px] leading-none text-fg-muted hover:bg-surface-3 disabled:opacity-50',
              FOCUS_RING,
            )}
          >
            <span aria-hidden="true">▼</span>
          </button>
        </span>
      ) : null}

      {ownMessage === undefined ? null : (
        <p id={messageId} className="sr-only">
          {ownMessage}
        </p>
      )}
    </div>
  );
}
